import { AlimtalkMessageDoc, KakaoAlimtalkRecipientItemWithResult } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useCallback, useMemo, useState } from 'react';

import { formatDateForAgCell } from 'src/utils/ag-grid-util';

import DownloadRawDataCell from '../../atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellSyncMessageResultButton from '../../atomic-components/atoms/AgCell/AgCellSyncMessageResultButton';

import KakaotalkContentPopover from './AlimtalkContentPopover/AlimtalkContentPopover';

export type AlimtalkMessageTableRowData = Omit<AlimtalkMessageDoc<any>, 'recipientWithResultList'> &
  KakaoAlimtalkRecipientItemWithResult<any> & {
    recipientSeq: number;
    storeNickname: string;
  };

interface AlimtalkMessageTableProps {
  rowData: AlimtalkMessageTableRowData[];
}

const defaultColDef: ColDef<AlimtalkMessageTableRowData> = {
  flex: 1,
  sortable: true,
  resizable: true,
  filter: true,
};

const AlimtalkMessageTable: FC<AlimtalkMessageTableProps> = ({ rowData }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<AlimtalkMessageTableRowData> | null>(null);

  const columnDefs: ColDef<AlimtalkMessageTableRowData>[] = useMemo(() => {
    return [
      {
        field: 'senderKey',
        hide: true,
      },
      {
        field: '_id',
        hide: true,
      },
      {
        headerName: 'requestId',
        field: 'alimtalkPostResponseId',
        hide: true,
      },
      {
        headerName: '요청일시',
        field: '_timeCreate',
        valueFormatter: formatDateForAgCell('L월 d일(EEEEEE) HH:mm:ss'),
      },
      {
        headerName: '수신번호',
        field: 'recipientNo',
      },
      {
        headerName: '수신매장',
        field: 'storeNickname',
      },
      {
        headerName: '수신자 식별키',
        field: 'recipientGroupingKey',
        hide: true,
      },
      {
        headerName: '요청결과',
        valueGetter: ({ data }) => {
          const { result } = data as AlimtalkMessageTableRowData;
          if (result?.requestResult.result) {
            return result?.requestResult.result === 'success' ? '🟢 성공' : `❌ ${result?.requestResult.error}`;
          }
          return null;
        },
      },
      {
        headerName: '발송결과',
        valueGetter: ({ data }) => {
          const { result, requestDate } = data as AlimtalkMessageTableRowData;
          if (result?.messageResult?.resultCode) {
            return result?.messageResult?.resultCode === '1000' ? '🟢 성공' : `❌ ${result?.messageResult.error}`;
          }

          if (requestDate) {
            return `⏰ 예약발송: ${requestDate}`;
          }
          return null;
        },
        cellRenderer: (props: any) => {
          const { value, data } = props;
          if (value) return value;
          const { _id, alimtalkPostResponseId, recipientSeq, result } = data as AlimtalkMessageTableRowData;
          return alimtalkPostResponseId && result?.messageResult ? (
            <AgCellSyncMessageResultButton
              type='alimtalk'
              messageDocId={_id}
              requestId={alimtalkPostResponseId}
              recipientSeq={recipientSeq}
            />
          ) : (
            '⚠️ 요청 결과 없음'
          );
        },
      },
      {
        headerName: '발송내용',
        field: 'result.messageResult.content',
        cellRenderer: KakaotalkContentPopover,
        cellRendererParams: (props: any) => {
          const { result } = props.data as AlimtalkMessageTableRowData;
          if (result?.messageResult?.buttons) {
            return {
              buttons: result.messageResult.buttons,
            };
          }
        },
      },
      {
        headerName: '액션',
        cellRenderer: DownloadRawDataCell,
        hide: true,
      },
    ];
  }, []);

  const autoSizeAll = useCallback(() => {
    gridRef?.columnApi?.autoSizeAllColumns(true);
  }, [gridRef]);

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        ref={setGridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onGridReady={autoSizeAll}
        // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
        getRowId={(params) => `${params.data._id}-${params.data.recipientSeq}`}
        // 편집 완료후 스크롤 이동을 막는다.
        suppressScrollOnNewData={true}
        // 툴팁의 노출 속도
        tooltipShowDelay={0}
      />
    </div>
  );
};

export default AlimtalkMessageTable;
